import { useEffect, useState } from 'react';

import { queryClient } from 'config/query-client';
import { Input } from 'pages/Dialogs/styled-components/common-components';
import {
  createInvoiceNumbers,
  updateInvoiceNumbers,
} from 'services/account-settings';
import { message } from 'antd';
import useTranslation from 'customHooks/translate';
import { INVOICE_NUMBERS } from 'events/types';
import { InvoicePrefixesCustomError } from 'types';

import CustomDialog from './CustomDialog';

interface IProps {
  open: boolean;
  partOfInvoiceNameToUpdate?: any;
  handleClose: () => void;
}

const initialValues: {
  partOfInvoiceName: string;
  id?: number;
} = {
  partOfInvoiceName: '',
};

const InvoicePrefixesForm = ({
  open,
  handleClose,
  partOfInvoiceNameToUpdate,
}: IProps) => {
  const translation = useTranslation();

  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [partOfInvoiceName, setPartOfInvoiceName] = useState(initialValues);

  useEffect(() => {
    setPartOfInvoiceName(partOfInvoiceNameToUpdate || initialValues);
  }, [partOfInvoiceNameToUpdate]);

  const setOnValueChange = (event, propName, isSelect = false) => {
    event.persist && event.persist();
    setPartOfInvoiceName((prevState) => ({
      ...prevState,
      [propName]: isSelect ? event : event.target.value,
    }));
  };

  const handleSave = () => {
    const method = partOfInvoiceName.id
      ? updateInvoiceNumbers
      : createInvoiceNumbers;
    method(partOfInvoiceName)
      .then(() => {
        message.success(`${translation.messages.successAdded}`);
        queryClient.invalidateQueries({ queryKey: [INVOICE_NUMBERS] });
        handleClose();
      })
      .catch((error: InvoicePrefixesCustomError) => {
        error.response?.data?.Message &&
          message.error(error.response?.data?.Message);
        const errorMessageName = error.response?.data.errors
          ?.PartOfInvoiceName?.[0] as string | undefined;
        setNameError(errorMessageName || null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <CustomDialog
      isLoading={loading}
      buttonTitle={
        !!partOfInvoiceNameToUpdate
          ? `${translation.taxTypes.Update}`
          : `${translation.taxTypes.Create}`
      }
      open={open}
      handleClose={handleClose}
      onSubmit={handleSave}
      title={translation.invoiceChars.InvoiceChar}
    >
      <form>
        <div className="flex flex-col gap-[12px]">
          <label
            className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
            htmlFor="name"
          >
            {translation.invoiceChars.InvoiceChar} *
          </label>
          <Input
            className="d-block"
            type="text"
            maxLength={2}
            onChange={(event) => {
              event.target.value = event.target.value.replace(/[^A-Z]/g, '');
              setOnValueChange(event, 'partOfInvoiceName');
            }}
            pattern="[A-Z]"
            placeholder="AZ"
            value={partOfInvoiceName?.partOfInvoiceName || ''}
            style={{
              borderColor: nameError ? 'red' : '',
            }}
          />
          {nameError && (
            <span className="text-[12px] text-red-600">{nameError}</span>
          )}
        </div>
      </form>
    </CustomDialog>
  );
};

export default InvoicePrefixesForm;
